import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { toast } from "sonner";
import {
  Upload, Search, Plus, FileSpreadsheet, Zap,
  ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight,
  X, Filter as FilterIcon, Check, Download, Eye,
} from "lucide-react";
import AddRequirementDialog from "@/pages/AddRequirementDialog";
import ImportReviewDialog from "@/pages/inventory/ImportReviewDialog";
import WholesalePriceImportDialog from "@/pages/inventory/WholesalePriceImportDialog";
import { formatDateTime } from "@/lib/format";

const SORTABLE_KEYS = new Set(["product", "brand_name", "group_name", "mrp", "last_purchase_cost"]);

// Extracted SortIcon component to avoid unstable nested components
const SortIcon = ({ col, sortBy, sortDir }) => {
  if (!SORTABLE_KEYS.has(col)) return null;
  if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 inline-block ml-1 opacity-40" />;
  return sortDir === "asc"
    ? <ArrowUp className="w-3 h-3 inline-block ml-1 text-[#9E6D5B]" />
    : <ArrowDown className="w-3 h-3 inline-block ml-1 text-[#9E6D5B]" />;
};

// Extracted FacetPopover component to avoid unstable nested components
const FacetPopover = ({ label, value, setValue, open, setOpen, items, testid }) => (
  <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        className="h-9"
        data-testid={`${testid}-trigger`}
      >
        <FilterIcon className="w-3.5 h-3.5 mr-2" /> {label}
        {value && <span className="ml-2 text-xs text-muted-foreground">· {value}</span>}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="p-0 w-[280px]" align="start" data-testid={`${testid}-popover`}>
      <Command>
        <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
        <CommandList className="max-h-72">
          <CommandEmpty>None found.</CommandEmpty>
          <CommandGroup>
            {items.map((it) => (
              <CommandItem
                key={it.name}
                onSelect={() => { setValue(it.name); setOpen(false); }}
                data-testid={`${testid}-opt-${it.name}`}
                className="flex items-center justify-between"
              >
                <span className="truncate mr-2">{it.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                  {value === it.name && <Check className="w-3 h-3 text-[#9E6D5B]" />}
                  <span>{it.count ?? 0}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
);

// Extracted ProductCard component to avoid unstable nested components
const ProductCard = ({ p, stockMap, openAdd }) => {
  const productStocks = stockMap[p.id] || [];
  // Memoize per-product batch rendering data — avoids recomputing flatMap + filter on every parent re-render
  const batchRows = useMemo(() => {
    return productStocks
      .filter((s) => (s.batches || []).length > 0)
      .flatMap((s) => s.batches.map((b) => {
        const mrpLabel = b.mrp != null ? `₹${Number(b.mrp).toFixed(0)}` : "N/A";
        let expLabel = "N/A";
        if (b.expiry_date) {
          try {
            const d = new Date(b.expiry_date);
            if (!isNaN(d)) {
              expLabel = `${d.toLocaleString("en-US", { month: "short" })}'${String(d.getFullYear()).slice(-2)}`;
            }
          } catch { /* fallback */ }
        }
        const status = b.expiry_status;
        const statusClass =
          status === "expired" ? "bg-red-50 text-red-700 border-red-200"
          : status === "expiring_soon" ? "bg-orange-50 text-orange-700 border-orange-200"
          : status === "expiring_3months" ? "bg-yellow-50 text-yellow-700 border-yellow-200"
          : "bg-gray-50 text-gray-600 border-gray-200";
        return { batch: b, shop_name: s.shop_name, mrpLabel, expLabel, statusClass };
      }));
  }, [productStocks]);
  
  return (
    <div 
      className="border-b border-[hsl(var(--border))] bg-white hover:bg-[#fafafa] transition-colors"
      data-testid={`product-row-${p.id}`}
    >
      {/* Product Name - Full Width Header */}
      <div className="px-3 py-2 border-b border-[hsl(var(--border))]/50 bg-[#f8f8f8] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Link 
            to={`/inventory/products/${p.id}`}
            className="font-semibold text-[13px] text-foreground leading-tight hover:text-[#9E6D5B] hover:underline transition-colors"
            data-testid={`product-name-link-${p.id}`}
          >
            {p.product}
          </Link>
          {p.purchase_count >= 2 && (
            <span className="pill pill-high shrink-0 text-[10px]" title={`Purchased ${p.purchase_count} times`}>
              <Zap className="w-2.5 h-2.5" /> Fast
            </span>
          )}
          {p.purchase_count === 1 && (
            <span className="pill pill-normal shrink-0 text-[10px]" title="Purchased once">
              Repeat
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/inventory/products/${p.id}`}>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 px-2 text-[10px] shrink-0"
              data-testid={`view-product-btn-${p.id}`}
            >
              <Eye className="w-3 h-3 mr-1" /> View
            </Button>
          </Link>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => openAdd(p)} 
            className="h-6 px-2 text-[10px] shrink-0"
            data-testid={`add-req-btn-${p.id}`}
          >
            <Plus className="w-3 h-3 mr-1" /> Req
          </Button>
        </div>
      </div>

      {/* Details Grid - Compact Layout */}
      <div className="px-3 py-2">
        {/* Main Info Row */}
        <div className="grid grid-cols-12 gap-x-2 gap-y-1 text-[11px]">
          {/* Brand - 2 cols */}
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Brand</div>
            <div className="font-medium truncate" title={p.brand_name}>{p.brand_name || "—"}</div>
          </div>
          {/* Group - 2 cols */}
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Group</div>
            <div className="text-muted-foreground truncate" title={p.group_name}>{p.group_name || "—"}</div>
          </div>
          {/* Barcodes - 2 cols */}
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Barcode</div>
            <div className="font-mono-data text-[10px] leading-tight" title={`Auto: ${p.auto_barcode}${p.manual_barcode ? ` | Manual: ${p.manual_barcode}` : ''}`}>
              <div className="truncate">{p.auto_barcode}</div>
              {p.manual_barcode && (
                <div className="truncate text-muted-foreground">{p.manual_barcode}</div>
              )}
            </div>
          </div>
          {/* MRP - 1.5 cols */}
          <div className="col-span-1 text-right">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">MRP</div>
            <div className="font-mono-data font-semibold text-foreground">₹{Number(p.mrp || 0).toFixed(0)}</div>
          </div>
          {/* Last Cost - 1.5 cols */}
          <div className="col-span-1 text-right">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Cost</div>
            <div className="font-mono-data text-muted-foreground">₹{Number(p.last_purchase_cost || 0).toFixed(0)}</div>
          </div>
          {/* Lowest - 1 col */}
          <div className="col-span-1 text-right">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Low</div>
            <div className="font-mono-data text-emerald-600">₹{Number(p.lowest_cost || 0).toFixed(0)}</div>
          </div>
          {/* Avg - 1 col */}
          <div className="col-span-1 text-right">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Avg</div>
            <div className="font-mono-data text-muted-foreground">₹{Number(p.average_cost || 0).toFixed(0)}</div>
          </div>
          {/* Stock Summary - 2 cols */}
          <div className="col-span-2 text-right">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Stock</div>
            <div className="flex flex-wrap justify-end gap-0.5">
              {productStocks.length > 0 ? productStocks.map((s) => (
                <span
                  key={s.shop_id}
                  className={`px-1 py-0 rounded text-[9px] font-mono-data ${
                    s.quantity !== null
                      ? s.quantity > 0
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-red-50 text-red-700"
                      : "bg-gray-100 text-gray-400"
                  }`}
                  title={s.quantity !== null ? `${s.shop_name}: ${s.quantity}` : `${s.shop_name}: N/A`}
                >
                  {s.shop_name?.slice(0,3)}-{s.quantity ?? "N/A"}
                </span>
              )) : <span className="text-muted-foreground text-[9px]">—</span>}
            </div>
          </div>
        </div>

        {/* Batch Details Row - Only if batches exist */}
        {batchRows.length > 0 && (
          <div className="mt-1.5 pt-1.5 border-t border-dashed border-[hsl(var(--border))]/50">
            <div className="flex flex-wrap gap-1">
              {batchRows.map(({ batch: b, shop_name, mrpLabel, expLabel, statusClass }) => {
                  return (
                    <span
                      key={b.id}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-mono-data ${statusClass}`}
                      title={`${shop_name}: MRP ${mrpLabel} · Exp ${expLabel} · Qty ${b.quantity}`}
                    >
                      <span className="font-medium">{shop_name?.slice(0,3)}</span>
                      <span className="opacity-50">|</span>
                      <span>{mrpLabel}</span>
                      <span className="opacity-50">|</span>
                      <span>{expLabel}</span>
                      <span className="opacity-50">|</span>
                      <span className="font-semibold">×{b.quantity}</span>
                    </span>
                  );
                })}
            </div>
          </div>
        )}

        {/* Last Purchase Info */}
        {p.latest_purchase && p.latest_purchase.date && (
          <div className="mt-1 text-[10px] text-muted-foreground">
            {(() => {
              const lp = p.latest_purchase;
              const d = new Date(lp.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              d.setHours(0, 0, 0, 0);
              const days = Math.max(0, Math.round((today - d) / (1000 * 60 * 60 * 24)));
              return (
                <>
                  Last: <span className="font-mono-data text-foreground">{lp.quantity}</span>
                  {" @ "}
                  <span className="font-mono-data">₹{Number(lp.cost || 0).toFixed(0)}</span>
                  {days > 0 && <span> · {days}d ago</span>}
                  {lp.supplier_name && <span> · {lp.supplier_name}</span>}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Inventory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // Initialize search from URL param if present
  const [q, setQ] = useState(() => searchParams.get("search") || "");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("product");
  const [sortDir, setSortDir] = useState("asc");
  
  // Smart import states
  const [reviewOpen, setReviewOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [applying, setApplying] = useState(false);
  const [wholesaleImportOpen, setWholesaleImportOpen] = useState(false);

  const [brand, setBrand] = useState(null);
  const [group, setGroup] = useState(null);
  const [facets, setFacets] = useState({ brands: [], groups: [] });
  const [brandOpen, setBrandOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);

  const [invStats, setInvStats] = useState(null);
  const [stockMap, setStockMap] = useState({}); // product_id -> [{shop_name, quantity}, ...]

  const fileRef = useRef();

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/products/stats");
      setInvStats(data);
    } catch (_e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    api.get("/products/filters").then((r) => setFacets(r.data)).catch(() => {});
    loadStats();
  }, [loadStats]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        q,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (brand) params.brand = brand;
      if (group) params.group = group;
      const { data } = await api.get(`/products`, { params });
      setItems(data.items);
      setTotal(data.total);
      
      // Fetch stock for all products in the result
      if (data.items.length > 0) {
        const productIds = data.items.map((p) => p.id).join(",");
        api.get("/stock/batch", { params: { product_ids: productIds } })
          .then((r) => setStockMap(r.data.items || {}))
          .catch(() => setStockMap({}));
      } else {
        setStockMap({});
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, page, pageSize, sortBy, sortDir, brand, group]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 250);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  useEffect(() => {
    setPage(1);
  }, [q, sortBy, sortDir, pageSize, brand, group]);

  // Smart differential upload - analyze first, then show review dialog
  const onUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      // Step 1: Analyze the file (don't apply changes yet)
      const { data } = await api.post("/products/upload/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // If no changes detected, show message and skip review
      if (data.new_products_count === 0 && data.changed_products_count === 0) {
        toast.info(
          `No changes detected. ${data.unchanged_count} products unchanged, ${data.skipped_empty_barcode + data.skipped_duplicate_in_excel} rows skipped.`,
          { duration: 4000 }
        );
      } else {
        // Open review dialog with analysis data
        setAnalysisData(data);
        setReviewOpen(true);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Apply approved changes from review dialog
  const onApplyChanges = async (approvedData) => {
    setApplying(true);
    try {
      const { data } = await api.post("/products/upload/apply", approvedData);
      
      const summary = [];
      if (data.inserted > 0) summary.push(`+${data.inserted} new`);
      if (data.updated > 0) summary.push(`${data.updated} updated`);
      
      toast.success(`Import complete: ${summary.join(", ")}`, { duration: 4000 });
      
      setReviewOpen(false);
      setAnalysisData(null);
      fetchProducts();
      loadStats();
      api.get("/products/filters").then((r) => setFacets(r.data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.get("/products/export", {
        responseType: "blob",
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "inventory_export.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Inventory exported successfully!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const openAdd = (p) => { setSelected(p); setOpenDialog(true); };

  const onSort = (col) => {
    if (!SORTABLE_KEYS.has(col)) return;
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="fade-up" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Catalog</p>
          <h1 className="font-heading text-3xl tracking-tight">Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload your Excel catalog and mark requirements to restock.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[hsl(var(--border))] rounded-md overflow-hidden bg-white mb-5" data-testid="inventory-stats-card">
        <div className="p-5 md:border-r border-[hsl(var(--border))] border-b md:border-b-0">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total products</div>
          <div className="font-heading text-3xl tracking-tight font-mono-data" data-testid="inventory-total">
            {invStats?.total_products ?? "—"}
          </div>
        </div>
        <div className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Last product update</div>
          <div className="font-mono-data text-sm" data-testid="inventory-last-update">
            {invStats?.last_updated_at
              ? formatDateTime(invStats.last_updated_at)
              : <span className="text-muted-foreground italic">No uploads yet</span>}
          </div>
          {invStats?.last_updated_product && (
            <div className="text-xs text-muted-foreground mt-1 truncate" title={invStats.last_updated_product}>
              {invStats.last_updated_product}
            </div>
          )}
        </div>
      </div>

      {/* Upload row */}
      <Card className="p-5 mb-5 flex flex-col md:flex-row items-start md:items-center gap-4" data-testid="upload-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[hsl(var(--muted))] flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-[#9E6D5B]" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-medium text-sm">Upload product catalog</div>
            <div className="text-xs text-muted-foreground">
              .xls / .xlsx — columns: Mannual Barcode, Auto Barcode, Product, Group Name, MRP, Purchase Price, Last Purchase Cost, Brand Name
            </div>
          </div>
        </div>
        <div className="md:ml-auto flex items-center gap-2">
          <input
            type="file"
            ref={fileRef}
            accept=".xls,.xlsx"
            onChange={(e) => onUpload(e.target.files?.[0])}
            className="hidden"
            data-testid="file-input"
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Choose file"}
          </Button>
          <Button 
            variant="outline" 
            onClick={onDownload} 
            disabled={downloading}
            data-testid="download-btn"
          >
            <Download className="w-4 h-4 mr-2" /> {downloading ? "Exporting…" : "Download"}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setWholesaleImportOpen(true)}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="wholesale-import-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Wholesale Prices
          </Button>
        </div>
      </Card>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by manual barcode, auto barcode, product, group or brand…"
          className="pl-9 h-11 bg-white"
          data-testid="product-search-input"
        />
      </div>

      {/* Filters + column visibility */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <FacetPopover
          label="Brand"
          value={brand}
          setValue={setBrand}
          open={brandOpen}
          setOpen={setBrandOpen}
          items={facets.brands}
          testid="filter-brand"
        />
        <FacetPopover
          label="Group"
          value={group}
          setValue={setGroup}
          open={groupOpen}
          setOpen={setGroupOpen}
          items={facets.groups}
          testid="filter-group"
        />

        {brand && (
          <button
            onClick={() => setBrand(null)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#9E6D5B]/40 bg-[#9E6D5B]/10 text-xs text-[#9E6D5B] hover:bg-[#9E6D5B]/20 transition-colors"
            data-testid="active-brand-chip"
          >
            {brand}
            <X className="w-3 h-3" />
          </button>
        )}
        {group && (
          <button
            onClick={() => setGroup(null)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#9E6D5B]/40 bg-[#9E6D5B]/10 text-xs text-[#9E6D5B] hover:bg-[#9E6D5B]/20 transition-colors"
            data-testid="active-group-chip"
          >
            {group}
            <X className="w-3 h-3" />
          </button>
        )}
        {(brand || group) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setBrand(null); setGroup(null); }}
            data-testid="clear-filters-btn"
            className="h-8 text-xs"
          >
            Clear
          </Button>
        )}

        {/* Sort Controls */}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sort:</span>
          <button
            onClick={() => onSort("product")}
            className={`text-xs px-2 py-1 rounded ${sortBy === "product" ? "bg-[#9E6D5B]/10 text-[#9E6D5B]" : "text-muted-foreground hover:text-foreground"}`}
          >
            Name <SortIcon col="product" sortBy={sortBy} sortDir={sortDir} />
          </button>
          <button
            onClick={() => onSort("mrp")}
            className={`text-xs px-2 py-1 rounded ${sortBy === "mrp" ? "bg-[#9E6D5B]/10 text-[#9E6D5B]" : "text-muted-foreground hover:text-foreground"}`}
          >
            MRP <SortIcon col="mrp" sortBy={sortBy} sortDir={sortDir} />
          </button>
          <button
            onClick={() => onSort("brand_name")}
            className={`text-xs px-2 py-1 rounded ${sortBy === "brand_name" ? "bg-[#9E6D5B]/10 text-[#9E6D5B]" : "text-muted-foreground hover:text-foreground"}`}
          >
            Brand <SortIcon col="brand_name" sortBy={sortBy} sortDir={sortDir} />
          </button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground mb-2 flex items-center justify-between">
        <span data-testid="products-range">
          {total === 0 ? "No products" : `Showing ${rangeStart}–${rangeEnd} of ${total.toLocaleString()} products`}
        </span>
        {loading && <span>Loading…</span>}
      </div>

      {/* Product Cards List */}
      <Card className="overflow-hidden" data-testid="inventory-table-card">
        {items.length > 0 ? (
          <div>
            {items.map((p) => (
              <ProductCard key={p.id} p={p} stockMap={stockMap} openAdd={openAdd} />
            ))}
          </div>
        ) : !loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No products match your filters.
          </div>
        ) : null}
      </Card>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4" data-testid="pagination-bar">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Rows per page</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v))}>
            <SelectTrigger className="w-[90px] bg-white" data-testid="page-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[25, 50, 100, 200].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground font-mono-data" data-testid="page-indicator">
            Page {page} of {totalPages}
          </span>
          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev-btn">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next-btn">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <AddRequirementDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        product={selected}
        onAdded={() => fetchProducts()}
      />

      <ImportReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        analysisData={analysisData}
        onApply={onApplyChanges}
        applying={applying}
      />

      <WholesalePriceImportDialog
        open={wholesaleImportOpen}
        onOpenChange={setWholesaleImportOpen}
        onSuccess={() => fetchProducts()}
      />
    </div>
  );
}
